import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import { characterList } from "@/mocks/characters";
import { communityTopics } from "@/mocks/community";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import NewsCard from "@/components/feature/NewsCard";
import CharacterCard from "@/components/feature/CharacterCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const lower = keyword.toLowerCase();
  useDocumentTitle(
    keyword ? `搜索「${keyword}」 - 漫蛙2` : "搜索 - 漫蛙2动漫资讯平台",
    "在漫蛙2全站搜索动漫资讯、角色与社区话题。"
  );

  const matchedNews = useMemo(
    () =>
      newsList.filter(
        (n) => n.title.toLowerCase().includes(lower) || n.summary.toLowerCase().includes(lower) || n.tags.some((t) => t.toLowerCase().includes(lower))
      ),
    [lower]
  );

  const matchedCharacters = useMemo(
    () =>
      characterList.filter(
        (c) =>
          c.name.toLowerCase().includes(lower) ||
          c.nameEn.toLowerCase().includes(lower) ||
          c.work.toLowerCase().includes(lower) ||
          c.tags.some((t) => t.toLowerCase().includes(lower))
      ),
    [lower]
  );

  const matchedTopics = useMemo(
    () => communityTopics.filter((t) => t.title.toLowerCase().includes(lower) || t.content.toLowerCase().includes(lower)),
    [lower]
  );

  const total = matchedNews.length + matchedCharacters.length + matchedTopics.length;

  const hotNews = newsList.slice(0, 3);

  if (!keyword) {
    return (
      <div className="page-wrap py-16">
        <EmptyState
          icon="ri-search-line"
          title="请输入要搜索的关键词"
          description="可以搜索动漫资讯标题、角色名称或社区话题，例如「星环」「魔法少女」。"
          actionLabel="浏览最新资讯"
          actionTo="/news"
        />
      </div>
    );
  }

  return (
    <div className="pb-4">
      <section className="border-b border-background-200 bg-background-100">
        <div className="page-wrap py-8 md:py-10">
          <Breadcrumb items={[{ label: "搜索结果" }]} />
          <h1 className="mt-4 text-2xl font-extrabold text-foreground-950 md:text-3xl">
            搜索「<span className="text-primary-600">{keyword}</span>」
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            共找到 <strong className="text-foreground-900">{total}</strong> 条匹配结果，涵盖资讯、角色与社区话题。
          </p>
        </div>
      </section>

      <div className="page-wrap mt-8">
        {total === 0 ? (
          <>
            <EmptyState
              title="没有找到匹配的内容"
              description="试试更换关键词，或者浏览下面的热门推荐内容。"
              actionLabel="浏览最新资讯"
              actionTo="/news"
            />
            <section className="mt-10">
              <h2 className="text-lg font-bold text-foreground-950">热门内容推荐</h2>
              <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                {hotNews.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          </>
        ) : (
          <div className="flex flex-col gap-12">
            {matchedNews.length > 0 && (
              <section>
                <div className="flex items-center gap-2">
                  <i className="ri-newspaper-line text-primary-600"></i>
                  <h2 className="text-lg font-bold text-foreground-950">资讯（{matchedNews.length}）</h2>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                  {matchedNews.map((item) => (
                    <NewsCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}

            {matchedCharacters.length > 0 && (
              <section>
                <div className="flex items-center gap-2">
                  <i className="ri-user-star-line text-accent-600"></i>
                  <h2 className="text-lg font-bold text-foreground-950">角色（{matchedCharacters.length}）</h2>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
                  {matchedCharacters.map((item) => (
                    <CharacterCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}

            {matchedTopics.length > 0 && (
              <section>
                <div className="flex items-center gap-2">
                  <i className="ri-chat-smile-2-line text-secondary-600"></i>
                  <h2 className="text-lg font-bold text-foreground-950">社区话题（{matchedTopics.length}）</h2>
                </div>
                <ul className="mt-4 flex flex-col gap-3">
                  {matchedTopics.map((topic) => (
                    <li key={topic.id}>
                      <Link
                        to={`/community/${topic.id}`}
                        className="group block rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
                            {topic.category}
                          </span>
                          <span className="text-xs text-foreground-400">{topic.createdAt}</span>
                        </div>
                        <h3 className="mt-2 text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
                          {topic.title}
                        </h3>
                        <p className="mt-1 line-clamp-1 text-xs text-foreground-600">{topic.content}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}